import { Localization } from "@viamap/viamap2-common";
import { Fragment, useContext } from "react";
import { GlassFoldUdBox, KeyValueLine, ViewButtonIconAsync } from "./PropertyComponents";
import { DC } from "src/dataCollector/DataCollector";
import { ExportData } from "src/managers/ExportData";
import { message } from "src/components/Message";
import { getTingbogDownloadErrorMessage } from "src/managers/TingbogDownload";
import { WFStoGeoJson } from "src/functions/WFSutils";
import { Persistence } from "src/managers/Persistence";
import { MapitStateContext, actionAddDataLayer } from "src/states/MapitState";
import { MIBIcon } from "src/componentsUtils/MIBIcon";

export function BilagsVisning({ servitutXDOC }: { servitutXDOC: Document | null }) {
   const { dispatch: mapitStateDispatch } = useContext(MapitStateContext);

   const isLikelyGmlBilag = (kode: string, beskrivelse: string, translatedKode: string | null): boolean => {
      if (kode === "38") {
         return true;
      }
      const fullText = `${beskrivelse || ""} ${translatedKode || ""}`.toLowerCase();
      return fullText.includes("gml");
   };

   const importGmlBilagToMap = async (ref: string, fallbackFileName: string) => {
      if (!ref) return;
      message.progress(Localization.getFormattedText("Loading {type} file...", { type: "GML" }));
      try {
         let { data, name } = await DC.TL.fetchServitutBilag(ref) || {};
         if (!data) {
            throw new Error("No appendix data received");
         }

         const gmlText = await data.text();
         const geoJsonLayers = WFStoGeoJson(gmlText).filter(({ geoJson }) => geoJson?.features && geoJson.features.length > 0);

         if (geoJsonLayers.length === 0) {
            message.error(Localization.getText("No features found in GML file."));
            return;
         }

         geoJsonLayers.forEach(({ layerName, geoJson }) => {
            Persistence.createLayersFromGeoJSON(layerName || "GML", geoJson, new Date(), (li) => {
               mapitStateDispatch(actionAddDataLayer(li, true));
            });
         });

         message.info(
            Localization.getFormattedText("Loaded {count} layers from {file}", {
               count: geoJsonLayers.length,
               file: name || fallbackFileName
            })
         );
      } catch (error) {
         console.error("Failed to import GML appendix", ref, error);
         message.error(error instanceof Error ? error.message : "GML import failed");
      } finally {
         message.progress();
      }
   };

   if (!(servitutXDOC instanceof Document)) {
      return <></>
   }
   let samling = servitutXDOC!.getElementsByTagName("BilagReferenceSamling")[0];
   if (!samling) return null;

   return (
      <GlassFoldUdBox title={Localization.getText("AppendixReference")} foldetUd={true}>
         {Array.from(samling.children).map((r: Element, idx: number) => {
            let ref = r.getElementsByTagName("BilagReference")[0]?.textContent || "";
            let kode = r.getElementsByTagName("BilagReferenceKode")[0]?.textContent || "";
            let beskrivelse = r.getElementsByTagName("Afsnit")[0]?.textContent || "";

            const linkUrl = ref ? `https://www.tinglysning.dk/tinglysning/rest/bilag/${ref}` : null;
            const lang = typeof Localization.getLanguage === 'function' ? Localization.getLanguage() : 'da';
            const translatedKode = kode ? (translationTableBilagsReferenceKode?.[lang]?.[kode] ?? translationTableBilagsReferenceKode?.['da']?.[kode] ?? kode) : null;
            const showDownloadAction = Boolean(ref);
            const showImportGmlAction = showDownloadAction && isLikelyGmlBilag(kode, beskrivelse, translatedKode);
            const downloadTitle = showImportGmlAction ? Localization.getText("Download file") : Localization.getText("Download PDF");

            return (
               <Fragment key={ref}>
                  <KeyValueLine valKey={
                     linkUrl ? (
                        <a href={linkUrl} target="_blank" rel="noopener noreferrer" >
                           Bilag {idx + 1}
                        </a>
                     ) : (
                        beskrivelse || Localization.getText("No data")
                     )
                  } value={<>
                     <div
                        style={{
                           display: "grid",
                           gridTemplateColumns: "minmax(0, 1fr) auto",
                           alignItems: "start",
                           columnGap: "0.5rem",
                           width: "100%",
                           textAlign: "left",
                           padding: 0
                        }}
                     >
                        <div style={{ minWidth: 0, padding: 0, overflowWrap: "anywhere", whiteSpace: "normal" }}>
                           {beskrivelse ? <div style={{ padding: 0 }}>{beskrivelse}</div> : null}
                           {translatedKode ? <div style={{ padding: 0 }}>{translatedKode}</div> : null}
                        </div>
                        {(showDownloadAction || showImportGmlAction) ? (
                           <div
                              style={{
                                 display: "flex",
                                 alignItems: "center",
                                 justifyContent: "flex-end",
                                 gap: "0.35rem",
                                 flexWrap: "wrap",
                                 padding: 0
                              }}
                           >
                              {showDownloadAction ? (
                                 <ViewButtonIconAsync title={downloadTitle} onClick={async () => {
                                    if (!ref) return;
                                    try {
                                       let { data, name } = await DC.TL.fetchServitutBilag(ref) || {};
                                       if (!data) {
                                          throw new Error("No appendix data received");
                                       }
                                       ExportData.downloadBlob(name || `bilag_${ref}.pdf`, data);
                                    } catch (error) {
                                       console.error("Failed to download Tingbog appendix", ref, error);
                                       message.error(getTingbogDownloadErrorMessage());
                                    }
                                 }} >
                                    <MIBIcon icon="pdf" />
                                 </ViewButtonIconAsync>
                              ) : null}
                              {showImportGmlAction ? (
                                 <ViewButtonIconAsync
                                    title={Localization.getText("ImageImport:MapOverlayButton")}
                                    onClick={async () => {
                                       await importGmlBilagToMap(ref, `bilag_${ref}.gml`);
                                    }}
                                 >
                                    <MIBIcon icon="addToMap" />
                                 </ViewButtonIconAsync>
                              ) : null}
                           </div>
                        ) : null}
                     </div>
                  </>} infoText={ref} />
               </Fragment>
            );
         })}
      </GlassFoldUdBox>
   );
}


// Translation table for BilagReferenceKode (updated)
const translationTableBilagsReferenceKode: any = {
   da: {
      "1": "Udlægsattest, udstedt før 25. marts 2008",
      "2": "Arrestattest, udstedt før 25. marts 2008",
      "3": "Ejendomsdom, med ankepåtegning, afsagt før 25. marts 2008",
      "4": "Modifikationsdom, med ankepåtegning, afsagt før 25. marts 2008",
      "5": "Auktionsskøde med evt. transportskøde",
      "6": "Bobestyrerattest, udstedt før 25. marts 2008",
      "7": "Kuratorattest, udstedt før 25. marts 2008",
      "8": "Tilladelse til erhvervelse af fast ejendom i Danmark, udstedt af JM",
      "9": "Tilladelse til erhvervelse, udstedt af Statsamt",
      "10": "Tilladelse fra Skov- og Naturstyrelsen om selskabs erhvervelse af fast ejendom",
      "11": "Dokumentation fra menighedsråd om tegningsreglerne for rådet",
      "12": "Attestation fra Direktoratet for Fødevareerhverv om behørig afgivelse af erklæringer i medfør af landbrugslovens erhvervsbestemmelser",
      "13": "Godkendelse fra Kirkeministeriet eller Stiftsøvrigheden til salg eller pantsætning af jordelig kirkelig gods",
      "14": "Tilladelse til erhvervelse af arbejderbolig",
      "15": "Stiftelsesdokumenter for selskab under stiftelse",
      "16": "Anden dokumentation for, at anmeldelse af nystiftet selskab er sket overfor Erhvervs- og Selskabsstyrelsen indenfor de i lovens krævede frister",
      "17": "Kommunalbestyrelsens samtykke til salg af ejendom fra Almen boligorganisation",
      "18": "Matrikelkort med attestationen \"e-rids\" (ved salg af nyudstykket parcel)",
      "19": "Dokumentation for generalforsamling om tegningsregler m.v. for forening",
      "20": "Vedtægter m.v. for forening",
      "21": "Interessentskabskontrakt med tegningsregler m.v.",
      "22": "Dispensation eller tilladelse fra Jordbrugskommissionen vedr. erhvervelse af landbrugsejendom",
      "23": "Skiftefuldmagter, udstedt før 25. marts 2008",
      "24": "Notering af umyndiggørelse på fast ejendom",
      "25": "Notering af ægtepagt som særeje",
      "26": "Tinglysning af ægtepagt som adkomst",
      "27": "Meddelelse om konkurs, udstedt før 25. marts 2008",
      "28": "Samme vedr. gældssanering, tvangsakkord",
      "29": "Tinglysning af vielsesattest rådighedsindskrænkende",
      "30": "Prokura, gældende før 25. marts 2008",
      "31": "Samtykke fra indehaver af forkøbsret eller tilbagekøbsret",
      "32": "Original bebestyrerattest, hvis en ejendom sælges af bobestyrer",
      "33": "Original skifteretsattest, hvis ejendom sælges af et dødsbo",
      "34": "Godkendelse fra kommunalbestyrelsen til tinglysning af servitut, jf. planlovens § 42",
      "35": "Samtykke fra bortforpagtning af landbrugsejendom fra Direktoratet for Fødevareerhverv",
      "36": "Godkendelse af pantsætning fra værge",
      "37": "Godkendelse af pantsætning fra Almennyttige boligselskaber",
      "38": "GML-dokument med koordinater til servitutter og bygning på lejet grund",
      "80": "Andet",
      "90": "Intern brug"
   },
   en: {
      "1": "Certificate of Execution issued prior to 25 March 2008",
      "2": "Certificate of Attachment issued prior to 25 March 2008",
      "3": "Judgment concerning real property, with endorsement of appeal, rendered prior to 25 March 2008",
      "4": "Modification judgment, with endorsement of appeal, rendered prior to 25 March 2008",
      "5": "Deed of Auction, including any Deed of Assignment",
      "6": "Certificate of Estate Administration issued prior to 25 March 2008",
      "7": "Trustee’s Certificate issued prior to 25 March 2008",
      "8": "Permit for acquisition of real property in Denmark issued by the Ministry of Justice",
      "9": "Permit for acquisition issued by the County Authority (Statsamt)",
      "10": "Authorization from the Danish Forest and Nature Agency regarding corporate acquisition of real property",
      "11": "Documentation from the Parish Council concerning the Council’s signing authority",
      "12": "Attestation from the Directorate for Food Business confirming proper submission of declarations pursuant to the agricultural law’s business provisions",
      "13": "Approval from the Ministry of Ecclesiastical Affairs or the Diocesan Authority for sale or mortgaging of church-owned land",
      "14": "Permit for acquisition of workers’ housing",
      "15": "Founding documents for a company under incorporation",
      "16": "Other documentation evidencing that notification of a newly incorporated company has been duly submitted to the Danish Commerce and Companies Agency within the statutory deadlines",
      "17": "Consent from the Municipal Council for sale of property by a Public Housing Organization",
      "18": "Cadastral map with the endorsement 'e-plan' (for sale of newly subdivided parcel)",
      "19": "Documentation from the general assembly concerning the association’s signing authority, etc.",
      "20": "Articles of Association and related documents for an association",
      "21": "Partnership Agreement including signing authority, etc.",
      "22": "Exemption or permit from the Agricultural Commission concerning acquisition of agricultural property",
      "23": "Probate Power of Attorney issued prior to 25 March 2008",
      "24": "Registration of legal incapacitation relating to real property",
      "25": "Registration of prenuptial agreement establishing separate property",
      "26": "Registration of prenuptial agreement as title",
      "27": "Notice of bankruptcy issued prior to 25 March 2008",
      "28": "Same concerning debt restructuring or compulsory composition",
      "29": "Registration of marriage certificate restricting right of disposal",
      "30": "Power of Procuration valid prior to 25 March 2008",
      "31": "Consent from holder of pre-emption or repurchase right",
      "32": "Original Certificate of Estate Administration where property is sold by an estate administrator",
      "33": "Original Certificate from the Probate Court where property is sold by an estate",
      "34": "Approval from the Municipal Council for registration of easement pursuant to section 42 of the Danish Planning Act",
      "35": "Consent from the Directorate for Food Business concerning lease of agricultural property",
      "36": "Approval of mortgage granted by legal guardian",
      "37": "Approval of mortgage granted by non-profit housing associations",
      "38": "GML document containing coordinates for easements and buildings on leased land",
      "80": "Other",
      "90": "Internal use"
   }
}
